import React, { useState } from 'react';
import {
  Box,
  Button,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ApiIcon from '@material-ui/icons/Extension';
import CatalogIcon from '@material-ui/icons/Apps';
import ComponentIcon from '@material-ui/icons/Memory';
// The same as on the page of a department, see the entity presentation in
// apis.ts. A domain, which groups systems, gets the tree instead.
import DepartmentIcon from '@material-ui/icons/Apartment';
import DomainIcon from '@material-ui/icons/AccountTree';
import ResourceIcon from '@material-ui/icons/Work';
import ScopeIcon from '@material-ui/icons/Archive';
import ServiceIcon from '@material-ui/icons/ShoppingCart';
import TeamIcon from '@material-ui/icons/People';
// The icon of a group in general, the same as in every link to one.
import GroupsIcon from '@material-ui/icons/VpnKey';
import SystemIcon from '@material-ui/icons/Category';
import UserIcon from '@material-ui/icons/Person';
import { IconComponent, useApi } from '@backstage/core-plugin-api';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  Link,
  SidebarDivider,
  SidebarItem,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { useLocation } from 'react-router-dom';
import { iconOfType } from '../../apis';
import {
  PinSubmenuButton,
  PinnedSubmenuPanel,
  usePinnedSubmenu,
} from './PinnedSubmenu';

/**
 * Readable names of the types the providers put on what they emit. A type
 * that is not in here - most of what teams write into their own catalog
 * files - is shown as it is.
 */
const TYPE_TITLES: Record<string, string> = {
  'aws-account': 'AWS accounts',
  'azure-subscription': 'Azure subscriptions',
  'oci-account': 'OCI accounts',
  'gcp-project': 'GCP projects',
  // Short enough for the flyout, which cuts "Kubernetes cluster slices".
  'k8s-cluster': 'K8s clusters',
  'k8s-cluster-slice': 'K8s cluster slices',
  'k8s-namespace': 'K8s namespaces',
  'k8s-deployment': 'K8s deployments',
  airflow: 'Airflow',
};

/** Types of a kind that are shown together in its fold-out, under a heading. */
type TypeGroup = { title: string; types: string[] };

/**
 * How the fold-out of Resources is laid out. A type in none of the groups - one
 * that turns up in the catalog later - is shown under "Other" at the end.
 */
const RESOURCE_TYPE_GROUPS: TypeGroup[] = [
  {
    title: 'Cloud accounts',
    types: ['aws-account', 'azure-subscription', 'gcp-project', 'oci-account'],
  },
  {
    title: 'Kubernetes',
    types: [
      'k8s-cluster',
      'k8s-cluster-slice',
      'k8s-namespace',
      'k8s-deployment',
    ],
  },
];

/** Further catalog filters a link sets, by name, e.g. `serviceProvidersOnly`. */
type CatalogFilters = Record<string, string>;

/**
 * The catalog, listing one kind and, if given, one type of it, narrowed by
 * whatever further filters are given.
 *
 * `user=all` because the catalog starts out on what the reader owns, and a
 * menu entry that says "Resources" is read as all of them.
 */
export const catalogPath = (
  kind: string,
  type?: string,
  filters: CatalogFilters = {},
) => {
  const kindFilter = encodeURIComponent(kind.toLocaleLowerCase('en-US'));
  const typeFilter = type ? `&filters[type]=${encodeURIComponent(type)}` : '';
  const furtherFilters = Object.entries(filters)
    .map(([name, value]) => `&filters[${name}]=${encodeURIComponent(value)}`)
    .join('');
  return `catalog?filters[kind]=${kindFilter}${typeFilter}${furtherFilters}&filters[user]=all`;
};

/**
 * Props for {@link CatalogKindItem}.
 */
type CatalogKindItemProps = {
  title: string;
  kind: string;
  icon: IconComponent;
  /**
   * The one type of the kind the entry stands for, e.g. the teams among the
   * groups. Such an entry lists that type and is highlighted for it only.
   */
  type?: string;
  /**
   * The types other entries stand for, which this one is then not highlighted
   * for - Groups, next to Departments and Teams.
   */
  otherThan?: string[];
  /**
   * Whether the entry folds out into the types of the kind, if it has more
   * than one. Off for a kind nobody browses by type: the entry is then a plain
   * link, and nothing is asked of the catalog for it.
   */
  foldOut?: boolean;
  /**
   * Headed groups to lay the fold-out out in. Left out, the types follow each
   * other without headings.
   */
  typeGroups?: TypeGroup[];
  /**
   * Further filters every link of the entry sets. Another entry that lists
   * the same kind under a filter of its own - Service Providers, of Groups -
   * would otherwise be highlighted together with this one.
   */
  filters?: CatalogFilters;
};

/** Stands for the "All" row of a fold-out, the kind without a type picked. */
const ALL_TYPES = '';

// The look of `SidebarSubmenuItem`, which this stands in for - see there.
const useKindItemStyles = makeStyles(theme => ({
  item: {
    height: 48,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textTransform: 'none',
    color: theme.palette.navigation.color,
    padding: theme.spacing(2.5),
    position: 'relative',
    '&:hover': {
      background:
        theme.palette.navigation.navItem?.hoverBackground || '#6f6f6f',
      color: theme.palette.navigation.selectedColor,
    },
  },
  selected: {
    background: '#6f6f6f',
    color: theme.palette.common.white,
  },
  label: {
    margin: theme.spacing(1.75),
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.body2.fontSize,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.5,
  },
  arrow: {
    position: 'absolute',
    right: 21,
  },
  dropdown: {
    display: 'flex',
    flexDirection: 'column',
  },
  dropdownItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 1, 1.25, 4),
    color: theme.palette.navigation.color,
    '&:hover': {
      background:
        theme.palette.navigation.navItem?.hoverBackground || '#6f6f6f',
    },
  },
  dropdownText: {
    color: theme.palette.navigation.color,
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.body2.fontSize,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dropdownHeading: {
    color: theme.palette.navigation.color,
    opacity: 0.7,
    padding: theme.spacing(1, 1, 0, 4),
    lineHeight: 2,
  },
  dropdownIcon: {
    display: 'flex',
    flexShrink: 0,
  },
  dropdownTextSelected: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold as number,
  },
}));

/** `/catalog/:namespace/:kind/:name`, the page of an entity. */
const ENTITY_PAGE = /^\/catalog\/([^/]+)\/([^/]+)\/([^/]+)/;

/**
 * An entry of the Catalog flyout for one kind, which folds out into its types
 * when the kind has more than one.
 *
 * The types are asked of the catalog rather than written down here: most
 * components come from catalog files the teams write themselves, so nobody
 * here knows their types, and the menu matches what is there. The flyout is
 * only rendered while it is open, so the question is only asked then.
 *
 * Drawn here rather than by `SidebarSubmenuItem`, which looks the same but
 * only ever highlights the entry itself, by matching its links against the
 * url: neither the type picked nor the page of an entity - whose url names no
 * type - can be shown with it.
 */
export const CatalogKindItem = (props: Readonly<CatalogKindItemProps>) => {
  const {
    title,
    kind,
    icon: Icon,
    type,
    otherThan,
    foldOut = true,
    typeGroups,
    filters = {},
  } = props;
  const classes = useKindItemStyles();
  const catalogApi = useApi(catalogApiRef);
  const location = useLocation();
  const kindName = kind.toLocaleLowerCase('en-US');

  // The listing of the kind. A filter the page has not put into the url yet
  // is taken to agree.
  const query = new URLSearchParams(location.search);
  const listingKind =
    location.pathname.endsWith('/catalog') &&
    query.get('filters[kind]') === kindName &&
    Object.entries(filters).every(
      ([name, value]) => (query.get(`filters[${name}]`) ?? value) === value,
    );

  // The page of an entity of the kind, whose type is looked up where the
  // highlight depends on it.
  const entityPage = location.pathname.match(ENTITY_PAGE);
  const entityRef =
    entityPage &&
    decodeURIComponent(entityPage[2]).toLocaleLowerCase('en-US') === kindName
      ? {
          kind,
          namespace: decodeURIComponent(entityPage[1]),
          name: decodeURIComponent(entityPage[3]),
        }
      : undefined;
  const byType = foldOut || type !== undefined || otherThan !== undefined;
  const { value: entityType } = useAsync(async () => {
    if (!entityRef || !byType) {
      return undefined;
    }
    const entity = await catalogApi.getEntityByRef(entityRef);
    return entity?.spec?.type as string | undefined;
  }, [catalogApi, byType, entityRef?.namespace, entityRef?.name]);

  const { value: facetTypes } = useAsync(async () => {
    if (!foldOut || type !== undefined) {
      return undefined;
    }
    const { facets } = await catalogApi.getEntityFacets({
      facets: ['spec.type'],
      filter: { kind },
    });
    return facets['spec.type']
      .map(({ value }) => value)
      .sort((a, b) => (TYPE_TITLES[a] ?? a).localeCompare(TYPE_TITLES[b] ?? b));
  }, [catalogApi, kind, foldOut, type]);

  const activeType = listingKind
    ? (query.get('filters[type]') ?? ALL_TYPES)
    : entityType;
  // Before the type of an entity is in, an entry that goes by type is left
  // alone rather than highlighted on a guess.
  const showsKind = listingKind || entityRef !== undefined;
  let active = showsKind;
  if (type !== undefined) {
    active = showsKind && activeType === type;
  } else if (otherThan !== undefined) {
    active =
      showsKind && activeType !== undefined && !otherThan.includes(activeType);
  }

  // Open on what is shown, so the type that is picked shows as such - until
  // the reader opens or closes it. The type of an entity comes in late, which
  // is why this is worked out on every render rather than once.
  const [toggled, setToggled] = useState<boolean>();
  const open = toggled ?? active;

  // A kind of one type has nothing to fold out, and clicking an entry that
  // folds out no longer opens the listing - "All" stands in for it. With
  // groups, every group that has a type in the catalog gets its heading, and
  // whatever is in none of them comes last under "Other".
  type DropdownRow = { heading: string } | { type: string; title: string };
  const rowOf = (facetType: string): DropdownRow => ({
    type: facetType,
    title: TYPE_TITLES[facetType] ?? facetType,
  });
  let dropdownItems: DropdownRow[] | undefined;
  if (facetTypes && facetTypes.length > 1) {
    dropdownItems = [{ type: ALL_TYPES, title: 'All' }];
    if (typeGroups) {
      const grouped = typeGroups.flatMap(group => group.types);
      for (const group of typeGroups) {
        const present = facetTypes.filter(it => group.types.includes(it));
        if (present.length > 0) {
          dropdownItems.push({ heading: group.title }, ...present.map(rowOf));
        }
      }
      const rest = facetTypes.filter(it => !grouped.includes(it));
      if (rest.length > 0) {
        dropdownItems.push({ heading: 'Other' }, ...rest.map(rowOf));
      }
    } else {
      dropdownItems.push(...facetTypes.map(rowOf));
    }
  }

  const label = (
    <Typography variant="subtitle1" component="span" className={classes.label}>
      {title}
    </Typography>
  );

  // The full name on hover, for whatever the flyout cuts short - the same
  // delay as on Backstage's own entries.
  const withTooltip = (
    name: string,
    element: React.ReactElement,
    key?: string,
  ) => (
    <Tooltip key={key} title={name} enterDelay={500} enterNextDelay={500}>
      {element}
    </Tooltip>
  );

  if (!dropdownItems) {
    return withTooltip(
      title,
      <Link
        to={catalogPath(kind, type, filters)}
        underline="none"
        className={`${classes.item} ${active ? classes.selected : ''}`}
        aria-current={active ? 'page' : undefined}
      >
        <Icon fontSize="small" />
        {label}
      </Link>,
    );
  }

  return (
    <Box width="100%">
      {withTooltip(
        title,
        <Button
          className={`${classes.item} ${active ? classes.selected : ''}`}
          onClick={() => setToggled(!open)}
          aria-expanded={open}
          aria-current={active ? 'true' : undefined}
        >
          <Icon fontSize="small" />
          {label}
          {open ? (
            <ArrowDropUpIcon className={classes.arrow} />
          ) : (
            <ArrowDropDownIcon className={classes.arrow} />
          )}
        </Button>,
      )}
      {open && (
        <Box className={classes.dropdown}>
          {dropdownItems.map(item => {
            if ('heading' in item) {
              return (
                <Typography
                  key={`heading-${item.heading}`}
                  variant="overline"
                  component="div"
                  className={classes.dropdownHeading}
                >
                  {item.heading}
                </Typography>
              );
            }
            const selected = active && item.type === activeType;
            // The icon the entities of the type are linked with, where the app
            // gives them one of their own, and the icon of the kind otherwise.
            const TypeIcon = iconOfType(kind, item.type) ?? Icon;
            return withTooltip(
              item.title,
              <Link
                to={catalogPath(kind, item.type || undefined, filters)}
                underline="none"
                className={classes.dropdownItem}
                aria-current={selected ? 'page' : undefined}
              >
                <span className={classes.dropdownIcon}>
                  <TypeIcon fontSize="small" />
                </span>
                <Typography
                  component="span"
                  className={`${classes.dropdownText} ${
                    selected ? classes.dropdownTextSelected : ''
                  }`}
                >
                  {item.title}
                </Typography>
              </Link>,
              item.type,
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const useHeaderStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.navigation.color,
    opacity: 0.7,
    padding: theme.spacing(1.5, 2.5, 0.5),
  },
}));

/** Heads a section of the Catalog flyout. */
export const CatalogSubmenuHeader = (props: { title: string }) => {
  const classes = useHeaderStyles();
  return (
    <Typography variant="overline" component="div" className={classes.root}>
      {props.title}
    </Typography>
  );
};

/**
 * What the Catalog menu offers, by layer. The same in the flyout and in the
 * pinned panel.
 */
const CatalogMenuEntries = () => (
  <>
    <CatalogSubmenuHeader title="Business" />
    <CatalogKindItem
      title="Users"
      kind="User"
      icon={UserIcon}
      foldOut={false}
    />
    <CatalogKindItem
      title="Teams"
      kind="Group"
      type="team"
      icon={TeamIcon}
      foldOut={false}
      filters={{ serviceProvidersOnly: 'false' }}
    />
    <CatalogKindItem
      title="Departments"
      kind="Group"
      type="department"
      icon={DepartmentIcon}
      foldOut={false}
      filters={{ serviceProvidersOnly: 'false' }}
    />
    <CatalogKindItem
      title="Domains"
      kind="Domain"
      icon={DomainIcon}
      foldOut={false}
    />
    <CatalogKindItem
      title="Systems"
      kind="System"
      icon={SystemIcon}
      foldOut={false}
    />
    <CatalogKindItem
      title="Products"
      kind="Product"
      icon={ScopeIcon}
      foldOut={false}
    />
    <SidebarDivider />
    <CatalogSubmenuHeader title="Software" />
    {/* Top down, the way the layers stack: what is offered, the interface it
        is reached through, what implements it. */}
    <CatalogKindItem
      title="Services"
      kind="Service"
      icon={ServiceIcon}
      foldOut={false}
    />
    <CatalogKindItem title="APIs" kind="API" icon={ApiIcon} foldOut={false} />
    <CatalogKindItem title="Components" kind="Component" icon={ComponentIcon} />
    <SidebarDivider />
    <CatalogSubmenuHeader title="Infrastructure" />
    {/* The service providers, the teams that provide services to the
        platform, see ServiceProvidersOnlyPicker. */}
    <FeatureFlagged with="platform-catalog">
      <SidebarSubmenuItem
        title="Service Providers"
        to={catalogPath('group', 'team', { serviceProvidersOnly: 'true' })}
        icon={TeamIcon}
      />
    </FeatureFlagged>
    {/* The Entra groups, which grant access to the resources below. The
        listing starts on them; the type filter there reaches the other
        groups. */}
    <CatalogKindItem
      title="Groups"
      kind="Group"
      type="ad-group"
      icon={GroupsIcon}
      foldOut={false}
      filters={{ serviceProvidersOnly: 'false' }}
    />
    <CatalogKindItem
      title="Resources"
      kind="Resource"
      icon={ResourceIcon}
      typeGroups={RESOURCE_TYPE_GROUPS}
    />
  </>
);

/**
 * Whether the page at this path is one of the Catalog menu: the listing, and
 * the page of an entity in it.
 */
export const isCatalogPage = (pathname: string) =>
  pathname === '/catalog' || pathname.startsWith('/catalog/');

/**
 * The Catalog entry of the sidebar, with its flyout - or without, while
 * {@link PinnedCatalogMenu} shows the menu pinned instead.
 */
export const CatalogSidebarItem = () => {
  const { shown } = usePinnedSubmenu();
  return (
    <SidebarItem icon={CatalogIcon} to="catalog" text="Catalog">
      {!shown && (
        <SidebarSubmenu title="Catalog">
          <PinSubmenuButton />
          <CatalogMenuEntries />
        </SidebarSubmenu>
      )}
    </SidebarItem>
  );
};

/** The Catalog menu, pinned next to the sidebar. */
export const PinnedCatalogMenu = () => {
  const location = useLocation();
  // Rendered anew when the listing changes kind, see CatalogKindItem - not on
  // every change of the url, which a search in the catalog makes per key.
  const kind = location.pathname.endsWith('/catalog')
    ? new URLSearchParams(location.search).get('filters[kind]')
    : null;
  return (
    <PinnedSubmenuPanel title="Catalog">
      <CatalogMenuEntries key={kind ?? ''} />
    </PinnedSubmenuPanel>
  );
};
